import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowCircleDownRoundedIcon from '@mui/icons-material/ArrowCircleDownRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import LayersIcon from '@mui/icons-material/Layers';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import {
	Badge,
	Box,
	Button,
	Card,
	CardContent,
	CardMedia,
	Chip,
	CircularProgress,
	Divider,
	IconButton,
	Popover,
	Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
	getPropostasSuperadas,
	marcarPropostaSuperadaVisualizada,
	NotificacaoPropostaSuperada,
} from '../../services/offer/getPropostasSuperadas';
import { colorPrimary } from '../../theme';

export const PropostasSuperadasMenu: React.FC = () => {
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [propostas, setPropostas] = useState<NotificacaoPropostaSuperada[]>([]);
	const [loading, setLoading] = useState(false);

	const aberto = Boolean(anchorEl);

	const carregarPropostas = async () => {
		setLoading(true);
		try {
			const lista = await getPropostasSuperadas();
			setPropostas(lista);
		} catch (error) {
			console.error('Erro ao carregar propostas superadas:', error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		carregarPropostas();
	}, []);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
		carregarPropostas();
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleNavigateToAd = (numIdAnuncio: number, isCombo: boolean, notificacaoId?: number) => {
		handleClose();
		if (notificacaoId) {
			marcarPropostaSuperadaVisualizada(notificacaoId);
			setPropostas((prev) => prev.filter((p) => p.id !== notificacaoId));
		}
		if (isCombo) {
			navigate(`/anuncios/detalhe/combo/${numIdAnuncio}`);
		} else {
			navigate(`/anuncios/detalhe/${numIdAnuncio}`);
		}
	};

	const formatarData = (iso?: string) => {
		if (!iso) return '';
		const data = new Date(iso);
		if (isNaN(data.getTime())) return '';
		const dia = data.getDate().toString().padStart(2, '0');
		const mes = (data.getMonth() + 1).toString().padStart(2, '0');
		const hora = data.getHours().toString().padStart(2, '0');
		const minuto = data.getMinutes().toString().padStart(2, '0');
		return `${dia}/${mes} às ${hora}:${minuto}`;
	};

	const getFotoUrl = (numIdAnuncio: number, foto?: string) => {
		if (!foto) return '';
		if (foto.startsWith('http')) return foto;
		return `https://www.canaldorepasse.com.br/anuncios/fotos/${numIdAnuncio}/${foto}`;
	};

	return (
		<>
			<IconButton
				onClick={handleClick}
				aria-label='Propostas superadas'
				sx={{
					cursor: 'pointer',
					display: 'flex',
					alignItems: 'center',
					fontSize: '28px',
					color: '#111111',
					marginLeft: { xs: '8px', md: '20px' },
					transition: 'all 0.2s ease',
					'&:hover': {
						color: colorPrimary,
						transform: 'scale(1.05)',
					},
					'@media (max-width:1200px)': {
						display: 'none',
					},
				}}
			>
				<Badge
					badgeContent={propostas.length}
					color='error'
					invisible={propostas.length === 0}
					sx={{
						'& .MuiBadge-badge': {
							fontSize: '0.72rem',
							height: 20,
							minWidth: 20,
							fontWeight: 'bold',
						},
					}}
				>
					<NotificationsNoneIcon sx={{ fontSize: 30 }} />
				</Badge>
			</IconButton>

			<Popover
				open={aberto}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				PaperProps={{
					sx: {
						width: { xs: 340, sm: 420 },
						maxHeight: 540,
						overflowY: 'auto',
						borderRadius: '16px',
						boxShadow: '0 12px 36px rgba(0,0,0,0.15)',
						border: '1px solid #E2E8F0',
						p: 0,
					},
				}}
			>
				{/* Header do Popover */}
				<Box
					sx={{
						p: '16px 20px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						borderBottom: '1px solid #F1F5F9',
						backgroundColor: '#FFFFFF',
						position: 'sticky',
						top: 0,
						zIndex: 10,
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography variant='h6' sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#0F172A' }}>
							Propostas Superadas
						</Typography>
						{propostas.length > 0 && (
							<Chip
								label={propostas.length}
								size='small'
								sx={{
									bgcolor: '#DC2626',
									color: '#FFFFFF',
									fontWeight: 'bold',
									height: 22,
									fontSize: '0.75rem',
								}}
							/>
						)}
					</Box>
				</Box>

				<Divider />

				{/* Conteúdo */}
				<Box sx={{ p: 1.5, backgroundColor: '#F8FAFC' }}>
					{loading && propostas.length === 0 ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
							<CircularProgress size={32} sx={{ color: colorPrimary }} />
						</Box>
					) : propostas.length === 0 ? (
						<Box
							sx={{
								textAlign: 'center',
								py: 5,
								px: 3,
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
							}}
						>
							<Box
								sx={{
									width: 64,
									height: 64,
									borderRadius: '50%',
									bgcolor: '#ECFDF5',
									border: '1.5px solid #A7F3D0',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									mb: 2,
								}}
							>
								<CheckCircleOutlineRoundedIcon sx={{ fontSize: 36, color: '#059669' }} />
							</Box>
							<Typography fontWeight={700} sx={{ color: '#0F172A', fontSize: '1rem', mb: 0.5 }}>
								Nenhum lance superado
							</Typography>
							<Typography variant='body2' sx={{ color: '#64748B', maxWidth: 280 }}>
								No momento você não possui propostas superadas. Seus lances continuam na liderança.
							</Typography>
						</Box>
					) : (
						propostas.map((item) => {
							const isCombo = Boolean(item.veiculos && item.veiculos.length > 1);
							const veiculo = item.veiculos && item.veiculos.length > 0 ? item.veiculos[0] : null;
							const titulo = isCombo
								? `Combo com ${item.veiculos.length} veículos: ${item.veiculos
										.map((v) => [v.marca, v.modelo].filter(Boolean).join(' '))
										.filter(Boolean)
										.join(' + ')}`
								: [veiculo?.marca, veiculo?.modelo].filter(Boolean).join(' ').trim();
							const placa = veiculo?.placa?.trim();
							const fotoUrl = getFotoUrl(item.num_id_anuncio, veiculo?.url_foto?.trim());
							const dataFormatada = formatarData(item.data_superacao);

							return (
								<Card
									key={item.id ?? item.num_id_anuncio}
									onClick={() => handleNavigateToAd(item.num_id_anuncio, isCombo, item.id)}
									sx={{
										mb: 1.5,
										borderRadius: '14px',
										border: isCombo ? '1.5px solid rgba(99, 102, 241, 0.4)' : '1px solid #E2E8F0',
										boxShadow: isCombo
											? '0 3px 10px rgba(99, 102, 241, 0.12)'
											: '0 2px 8px rgba(0,0,0,0.04)',
										cursor: 'pointer',
										transition: 'all 0.2s ease',
										'&:hover': {
											boxShadow: isCombo
												? '0 8px 20px rgba(99, 102, 241, 0.2)'
												: '0 6px 16px rgba(0,0,0,0.08)',
											borderColor: isCombo ? '#6366F1' : '#CBD5E1',
											transform: 'translateY(-2px)',
										},
									}}
								>
									<CardContent sx={{ p: '14px !important' }}>
										{/* Cabeçalho do Card: Status + Badge Combo + Data */}
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
												mb: 1,
											}}
										>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
												<Box
													sx={{
														display: 'inline-flex',
														alignItems: 'center',
														gap: 0.5,
														bgcolor: '#FEF2F2',
														border: '1px solid #FECACA',
														color: '#DC2626',
														px: 1.5,
														py: 0.4,
														borderRadius: '16px',
														fontSize: '0.72rem',
														fontWeight: 700,
													}}
												>
													<ArrowCircleDownRoundedIcon sx={{ fontSize: 14 }} />
													<span>Lance superado</span>
												</Box>

												{isCombo && (
													<Box
														sx={{
															display: 'inline-flex',
															alignItems: 'center',
															gap: 0.4,
															background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
															color: '#FFFFFF',
															px: 0.9,
															py: 0.4,
															borderRadius: '16px',
															fontSize: '0.68rem',
															fontWeight: 800,
															letterSpacing: 0.3,
															boxShadow: '0 2px 6px rgba(99, 102, 241, 0.3)',
														}}
													>
														<LayersIcon sx={{ fontSize: 13 }} />
														<span>COMBO • {item.veiculos.length} VEÍCULOS</span>
													</Box>
												)}
											</Box>

											
										</Box>
										{dataFormatada && (
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748B' }}>
													<AccessTimeRoundedIcon sx={{ fontSize: 13 }} />
													<Typography variant='caption' sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
														{dataFormatada}
													</Typography>
												</Box>
											)}
										{/* Imagem + Informações do Veículo */}
										<Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
											<Box sx={{ position: 'relative' }}>
												{fotoUrl ? (
													<CardMedia
														component='img'
														sx={{
															width: 100,
															height: 75,
															objectFit: 'cover',
															borderRadius: '10px',
															bgcolor: '#F1F5F9',
														}}
														src={fotoUrl}
														alt={titulo || 'Veículo'}
													/>
												) : (
													<Box
														sx={{
															width: 100,
															height: 75,
															borderRadius: '10px',
															bgcolor: '#F1F5F9',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															color: '#94A3B8',
														}}
													>
														<DirectionsCarOutlinedIcon sx={{ fontSize: 32 }} />
													</Box>
												)}

												{isCombo && (
													<Box
														sx={{
															position: 'absolute',
															bottom: 4,
															right: 4,
															bgcolor: 'rgba(0,0,0,0.75)',
															color: '#FFFFFF',
															borderRadius: '4px',
															px: 0.6,
															py: 0.2,
															fontSize: '0.62rem',
															fontWeight: 700,
															display: 'flex',
															alignItems: 'center',
															gap: 0.3,
														}}
													>
														<LayersIcon sx={{ fontSize: 10 }} />
														<span>{item.veiculos.length} un</span>
													</Box>
												)}
											</Box>

											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
													<Typography
														variant='caption'
														sx={{
															fontSize: '0.72rem',
															color: '#94A3B8',
															fontWeight: 600,
														}}
													>
														#{item.num_id_anuncio}
													</Typography>
													{isCombo && (
														<Box
															component='span'
															sx={{
																px: 0.6,
																py: 0.1,
																borderRadius: '4px',
																bgcolor: 'rgba(99, 102, 241, 0.12)',
																color: '#6366F1',
																fontSize: '0.65rem',
																fontWeight: 800,
															}}
														>
															PACOTE
														</Box>
													)}
												</Box>

												<Typography
													variant='subtitle2'
													sx={{
														fontWeight: 700,
														color: '#0F172A',
														fontSize: '0.92rem',
														lineHeight: 1.25,
														whiteSpace: 'nowrap',
														overflow: 'hidden',
														textOverflow: 'ellipsis',
													}}
												>
													{titulo || 'Veículo Canal do Repasse'}
												</Typography>

												{isCombo ? (
													<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.8 }}>
														{item.veiculos.map((v, i) =>
															v.placa ? (
																<Box
																	key={i}
																	component='span'
																	sx={{
																		px: 0.6,
																		py: 0.2,
																		borderRadius: '5px',
																		border: '1px solid #CBD5E1',
																		bgcolor: '#F8FAFC',
																		fontSize: '0.68rem',
																		fontWeight: 700,
																		letterSpacing: 0.4,
																		color: '#334155',
																	}}
																>
																	#{i + 1} {v.placa.toUpperCase()}
																</Box>
															) : null
														)}
													</Box>
												) : (
													placa && (
														<Box
															component='span'
															sx={{
																display: 'inline-block',
																mt: 0.8,
																px: 0.8,
																py: 0.2,
																borderRadius: '5px',
																border: '1px solid #CBD5E1',
																bgcolor: '#F8FAFC',
																fontSize: '0.72rem',
																fontWeight: 700,
																letterSpacing: 0.5,
																color: '#334155',
															}}
														>
															{placa.toUpperCase()}
														</Box>
													)
												)}
											</Box>
										</Box>

										{/* Bloco de Valor e Alerta */}
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
												bgcolor: '#F8FAFC',
												border: '1px solid #E2E8F0',
												borderRadius: '10px',
												p: '8px 12px',
												mb: 1.5,
											}}
										>
											<Box>
												<Typography
													variant='caption'
													sx={{
														fontSize: '0.65rem',
														fontWeight: 700,
														letterSpacing: 0.5,
														color: '#64748B',
														display: 'block',
													}}
												>
													{isCombo ? 'SEU LANCE NO COMBO COBERTO' : 'SEU LANCE COBERTO'}
												</Typography>
												<Typography
													variant='body2'
													sx={{
														fontSize: '1rem',
														fontWeight: 800,
														color: '#DC2626',
													}}
												>
													R$ {item.val_superado.toLocaleString('pt-BR', {
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													})}
												</Typography>
											</Box>

											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#C2410C' }}>
												<ErrorOutlineRoundedIcon sx={{ fontSize: 14 }} />
												<Typography variant='caption' sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
													Oferta coberta
												</Typography>
											</Box>
										</Box>

										{/* CTA - Botão de Novo Lance */}
										<Button
											variant='contained'
											fullWidth
											size='small'
											onClick={(e) => {
												e.stopPropagation();
												handleNavigateToAd(item.num_id_anuncio, isCombo, item.id);
											}}
											endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
											sx={{
												bgcolor: colorPrimary,
												color: '#FFFFFF',
												borderRadius: '8px',
												fontWeight: 700,
												fontSize: '0.8rem',
												textTransform: 'none',
												py: 0.8,
												'&:hover': {
													bgcolor: '#d35d19',
												},
											}}
										>
											{isCombo ? 'Dar novo lance no combo' : 'Dar novo lance'}
										</Button>
									</CardContent>
								</Card>
							);
						})
					)}
				</Box>
			</Popover>
		</>
	);
};
